import type { AnnotationDocument } from '@/lib/annotation/schema'

/**
 * A lightbox markup save as the offline queue holds it: `PATCH /media_objects/{id}` with a body
 * of `{ annotationData, annotationRequestUUID }` (patchMediaObjectAnnotation, lib/actions.ts).
 * The rotate commit (`/media_objects/{id}/rotate`) is a POST that bypasses the queue, and an
 * upload is a POST, so neither matches.
 */
export type QueuedMarkupSave = {
  mediaObjectId: string
  annotationRequestUUID: string
  annotationData: AnnotationDocument | null
}

export function markupSaveOf(
  method: unknown,
  url: unknown,
  body: unknown,
): QueuedMarkupSave | null {
  if (typeof method !== 'string' || method.toUpperCase() !== 'PATCH') return null
  const mediaObjectId = mediaObjectIdAddressedBy(url)
  if (!mediaObjectId) return null
  if (typeof body !== 'object' || body === null || !('annotationData' in body)) return null
  const { annotationData, annotationRequestUUID } = body as {
    annotationData: unknown
    annotationRequestUUID?: unknown
  }
  if (typeof annotationRequestUUID !== 'string' || annotationRequestUUID === '') return null
  return {
    mediaObjectId,
    annotationRequestUUID,
    annotationData: (annotationData ?? null) as AnnotationDocument | null,
  }
}

/**
 * The replay lane of a queued markup save. Every save draws a fresh `requestUUID`, so the
 * queue's own grouping never orders two saves of the same image; this key does.
 */
export function markupLaneOf(method: unknown, url: unknown, body: unknown): string | null {
  const save = markupSaveOf(method, url, body)
  return save ? `media-object-annotation:${save.mediaObjectId}` : null
}

/** The media object id of an attachment: its `@id` `/media_objects/<id>`, else its `id`. */
export function attachmentMediaObjectId(attachment: unknown): string | null {
  if (typeof attachment !== 'object' || attachment === null) return null
  const { '@id': iri, id } = attachment as { '@id'?: unknown; id?: unknown }
  if (typeof iri === 'string') {
    const match = /\/media_objects\/([^/?#]+)/.exec(iri)
    if (match) return match[1]
  }
  return typeof id === 'number' || (typeof id === 'string' && id !== '') ? String(id) : null
}

/** `<id>` when the URL's path ends in `/media_objects/<id>` — and nothing after it. */
function mediaObjectIdAddressedBy(url: unknown): string | null {
  if (typeof url !== 'string') return null
  let path: string
  try {
    path = new URL(url, 'http://localhost').pathname
  } catch {
    return null
  }
  const segments = path.split('/').filter(Boolean)
  const n = segments.length
  return n >= 2 && segments[n - 2] === 'media_objects' ? segments[n - 1] : null
}
